import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import config from "../../../config";
import style from "./style.scss";

// Cloudflare Turnstile script (loaded once, explicit render mode)
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Cloudflare global test sitekey (always passes; used as fallback so dev stacks render without config)
const TEST_SITE_KEY = "1x00000000000000000000AA";

let scriptPromise = null;
const loadTurnstile = () => {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = TURNSTILE_SCRIPT;
            script.async = true;
            script.onload = () => resolve(window.turnstile);
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
};

export const Captcha = React.forwardRef(function Captcha({ onChange, sitekey }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const tokenRef = useRef(null);
    const [error, setError] = useState(false);

    useImperativeHandle(ref, () => ({
        getValue: () => tokenRef.current,
        reset: () => {
            tokenRef.current = null;
            onChange?.(null);
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
            }
        }
    }));

    useEffect(() => {
        let mounted = true;
        loadTurnstile().then((turnstile) => {
            if (!mounted || !containerRef.current) {
                return;
            }
            widgetIdRef.current = turnstile.render(containerRef.current, {
                sitekey: sitekey || config?.TURNSTILE_SITE_KEY || TEST_SITE_KEY,
                callback: (token) => {
                    tokenRef.current = token;
                    setError(false);
                    onChange?.(token);
                },
                "expired-callback": () => {
                    tokenRef.current = null;
                    onChange?.(null);
                },
                "error-callback": () => {
                    tokenRef.current = null;
                    onChange?.(null);
                    setError(true);
                }
            });
        });
        return () => {
            mounted = false;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    return (
        <div className={style.base}>
            <div ref={containerRef} />
            {error ? <div className={style.error}>!</div> : null}
        </div>
    );
});
